/**
 * CMS OVERLAY SERVICE
 * 
 * Couche runtime pour fusionner:
 * 1. CMS Overrides (admin/DB) - priorité haute
 * 2. pageConfig (système) - priorité moyenne
 * 3. Defaults frontend - priorité basse
 * 
 * ARCHITECTURE:
 * CMS Overrides → Page Config → Hub/Listing Templates → UI Render
 * 
 * RÈGLES:
 * - CMS peut ajouter/supprimer/réordonner des blocs UI
 * - CMS ne peut PAS modifier Query Engine
 * - CMS ne peut PAS modifier State Engine
 * - CMS peut contrôler visibility des éléments globaux
 */

import { isBlockAllowed, getAllowedBlockTypes, isSystemOnlyBlock } from './cmsBlockRegistry';

// ============================================================================
// LEGACY COMPAT — These constants are derived from the registry.
// They exist ONLY for backward compatibility with external imports.
// Do NOT use them in new code. Use cmsBlockRegistry functions instead.
// ============================================================================
export const HUB_ALLOWED_BLOCKS = getAllowedBlockTypes('HUB');
export const LISTING_ALLOWED_BLOCKS = getAllowedBlockTypes('LISTING');
export const SYSTEM_ONLY_BLOCKS = [];

// ============================================================================
// STRUCTURE CMS OVERLAY
// ============================================================================

/**
 * Structure d'un CMS Page Override
 * @typedef {Object} CmsPageOverride
 * @property {string} pageSlug - Slug de la page (ex: 'nouveautes')
 * @property {boolean} enabled - Si les overrides sont actifs
 * @property {Object} globalSettings - Settings UI globaux
 * @property {Array} blocks - Blocs CMS à injecter
 * @property {Object} sections - Sections à activer/désactiver
 */

/**
 * Structure des global settings
 */
export const DEFAULT_GLOBAL_SETTINGS = {
  showHeader: true,
  showMenu: true,
  showFooter: true,
  headerVariant: 'default',
  menuVariant: 'default',
  footerVariant: 'default'
};

/**
 * Structure des sections par défaut
 */
export const DEFAULT_SECTIONS = {
  banner: { enabled: true, order: 0 },
  navigation: { enabled: true, order: 1 },
  content: { enabled: true, order: 2 },
  info: { enabled: true, order: 3 }
};

// ============================================================================
// FONCTIONS DE MERGE
// ============================================================================

/**
 * Fusionne les overrides CMS avec la config système
 * 
 * Priorité: CMS > pageConfig > defaults
 * 
 * @param {Object} pageConfig - Config statique depuis pageConfig.js
 * @param {Object} cmsOverrides - Overrides depuis la DB
 * @returns {Object} Config finale mergée
 */
export const mergePageConfig = (pageConfig, cmsOverrides) => {
  if (!pageConfig) return null;
  
  // Si pas d'overrides CMS, retourner la config système
  if (!cmsOverrides || !cmsOverrides.enabled) {
    return {
      ...pageConfig,
      _source: 'system',
      globalSettings: DEFAULT_GLOBAL_SETTINGS,
      sections: DEFAULT_SECTIONS,
      cmsBlocks: []
    };
  }
  
  // Fusionner avec priorité CMS
  return {
    ...pageConfig,
    _source: 'cms_merged',
    
    // Global settings (CMS override système)
    globalSettings: {
      ...DEFAULT_GLOBAL_SETTINGS,
      ...(cmsOverrides.globalSettings || {})
    },
    
    // Sections (CMS override système)
    sections: {
      ...DEFAULT_SECTIONS,
      ...(cmsOverrides.sections || {})
    },
    
    // Blocs CMS injectés (filtered via registry)
    cmsBlocks: filterAllowedBlocks(
      cmsOverrides.blocks || [],
      pageConfig.type === 'hub' ? HUB_ALLOWED_BLOCKS : LISTING_ALLOWED_BLOCKS,
      pageConfig.type === 'hub' ? 'HUB' : 'LISTING'
    ),
    
    // Metadata
    _cmsOverrideApplied: true,
    _cmsOverrideDate: new Date().toISOString()
  };
};

/**
 * Filtre les blocs pour ne garder que ceux autorisés (via registry)
 */
export const filterAllowedBlocks = (blocks, allowedTypes, pageType) => {
  // If pageType is provided, use registry directly; otherwise fallback to allowedTypes array
  return blocks.filter(block => {
    if (isSystemOnlyBlock(block.type)) {
      console.warn(`[CMS Overlay] Bloc "${block.type}" ignoré - géré par le système`);
      return false;
    }
    
    // Prefer registry-based check when pageType is available
    const allowed = pageType
      ? isBlockAllowed(block.type, pageType)
      : allowedTypes.includes(block.type);
    
    if (!allowed) {
      console.warn(`[CMS Overlay] Bloc "${block.type}" ignoré - non autorisé sur ce type de page`);
      return false;
    }
    
    return true;
  });
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convertit les blocs API en structure CMS compatible
 */
export const convertApiBlocksToCmsBlocks = (apiBlocks) => {
  if (!apiBlocks || !Array.isArray(apiBlocks)) return [];
  
  return apiBlocks
    .filter(block => block.is_active)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(block => ({
      id: block.id,
      type: block.type,
      content: block.content,
      order: block.order,
      zone: block.zone || 'content',
      is_active: block.is_active
    }));
};

/**
 * Groupe les blocs CMS par zone
 */
export const groupBlocksByZone = (blocks) => {
  const zones = {
    header: [],
    menu: [],
    content: [],
    footer: []
  };
  
  blocks.forEach(block => {
    const zone = block.zone || 'content';
    if (zones[zone]) {
      zones[zone].push(block);
    } else {
      zones.content.push(block);
    }
  });
  
  // Trier chaque zone par order
  Object.keys(zones).forEach(zone => {
    zones[zone].sort((a, b) => (a.order || 0) - (b.order || 0));
  });
  
  return zones;
};

/**
 * Détermine le slug CMS depuis une URL
 * /dashboard/nouveautes → /dashboard/nouveautes
 * /dashboard/nouveautes/en-cours → /dashboard/nouveautes (HUB parent)
 */
export const getCmsSlugFromPath = (pathname, pageType) => {
  // SCOPE ISOLATION FIX:
  // Pour les LISTING, on NE veut PAS le slug du HUB parent
  // Chaque page doit avoir son propre slug CMS
  // Si la page CMS n'existe pas, on retourne null (pas de fallback!)
  
  const parts = pathname.replace('/dashboard/', '').split('/');
  
  // ANCIEN CODE (CAUSAIT CONTAMINATION):
  // if (pageType === 'listing' && parts.length > 1) {
  //   return `/dashboard/${parts[0]}`; // ❌ Retournait le HUB parent
  // }
  
  // NOUVEAU CODE (ISOLATION STRICTE):
  // Retourner le pathname tel quel
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
};

/**
 * Vérifie si un bloc CMS peut être affiché sur une page (via registry)
 */
export const canShowBlockOnPage = (block, pageType) => {
  if (isSystemOnlyBlock(block.type)) return false;
  return isBlockAllowed(block.type, pageType);
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  mergePageConfig,
  filterAllowedBlocks,
  convertApiBlocksToCmsBlocks,
  groupBlocksByZone,
  getCmsSlugFromPath,
  canShowBlockOnPage,
  HUB_ALLOWED_BLOCKS,
  LISTING_ALLOWED_BLOCKS,
  SYSTEM_ONLY_BLOCKS,
  DEFAULT_GLOBAL_SETTINGS,
  DEFAULT_SECTIONS
};
